/**
 * Alpha Access API functions
 * Handles invite codes, waitlist, and wave management
 */
import { ENDPOINTS } from './endpoints';
import { getRequestBackendDataV2, postRequestBackendDataV2 } from './core';
import logger from '../utils/logger';

// ==================== PUBLIC ENDPOINTS (No Auth) ====================

function getBaseApiUrl() {
    const isLocalhost = window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1';

    return isLocalhost
        ? 'http://localhost:8085/api/v2/'
        : 'https://api.nanotradingcopilot.com/api/v2/';
}

/**
 * Get alpha system status (no auth required)
 */
export async function getAlphaSystemStatus() {
    try {
        const response = await fetch(getBaseApiUrl() + ENDPOINTS.ALPHA.STATUS);
        return await response.json();
    } catch (error) {
        logger.error('Error getting alpha status:', error);
        return { alphaEnabled: false, inviteRequired: false, waitlistEnabled: false, signupCapReached: false };
    }
}

/**
 * Validate an invite code (no auth required)
 */
export async function validateInviteCode(code) {
    try {
        const response = await fetch(getBaseApiUrl() + ENDPOINTS.ALPHA.VALIDATE(code));
        return await response.json();
    } catch (error) {
        logger.error('Error validating invite code:', error);
        return { valid: false, errorCode: 'NETWORK_ERROR' };
    }
}

/**
 * Join the waitlist (auth required)
 */
export async function joinWaitlist(auth, name, referralCode) {
    if (!auth?.currentUser) {
        return { success: false, errorCode: 'NOT_AUTHENTICATED' };
    }

    try {
        const idToken = await auth.currentUser.getIdToken(false);
        const response = await fetch(getBaseApiUrl() + ENDPOINTS.ALPHA.WAITLIST, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${idToken}`
            },
            body: JSON.stringify({ name, referralCode }),
        });

        return await response.json();
    } catch (error) {
        logger.error('Error joining waitlist:', error);
        return { success: false, errorCode: 'NETWORK_ERROR' };
    }
}

/**
 * Get waitlist status (auth required)
 */
export async function getWaitlistStatus(auth) {
    if (!auth?.currentUser) {
        return { error: 'Not authenticated' };
    }

    try {
        const idToken = await auth.currentUser.getIdToken(false);
        const response = await fetch(getBaseApiUrl() + ENDPOINTS.ALPHA.WAITLIST_STATUS, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${idToken}`
            }
        });
        if (response.status === 404) return null;
        return await response.json();
    } catch (error) {
        logger.error('Error getting waitlist status:', error);
        return { error: 'Network error' };
    }
}

/**
 * Redeem an invite code to get alpha access (auth required)
 */
export async function redeemInviteCode(auth, code) {
    if (!auth?.currentUser) {
        return { success: false, errorCode: 'NOT_AUTHENTICATED' };
    }

    try {
        const idToken = await auth.currentUser.getIdToken(false);
        const response = await fetch(getBaseApiUrl() + 'alpha/redeem', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${idToken}`
            },
            body: JSON.stringify({ code }),
        });

        return await response.json();
    } catch (error) {
        logger.error('Error redeeming code:', error);
        return { success: false, errorCode: 'NETWORK_ERROR' };
    }
}

// ==================== ADMIN ENDPOINTS ====================

// Waves

/**
 * Get all alpha waves (admin)
 */
export async function getAlphaWaves(auth, callback) {
    await getRequestBackendDataV2(auth, callback, ENDPOINTS.ALPHA.WAVES);
}

/**
 * Create a new wave (admin)
 */
export async function createAlphaWave(auth, callback, wave) {
    await postRequestBackendDataV2(auth, callback, ENDPOINTS.ALPHA.WAVES, wave);
}

/**
 * Update wave status (admin)
 */
export async function updateWaveStatus(auth, callback, waveId, status) {
    await postRequestBackendDataV2(auth, callback, ENDPOINTS.ALPHA.WAVE_STATUS(waveId), { status }, 'PUT');
}

// Codes

/**
 * Get all invite codes (admin)
 */
export async function getAlphaCodes(auth, callback, waveId = null) {
    const endpoint = waveId ? ENDPOINTS.ALPHA.CODES_BY_WAVE(waveId) : ENDPOINTS.ALPHA.CODES;
    await getRequestBackendDataV2(auth, callback, endpoint);
}

/**
 * Generate batch of invite codes (admin)
 */
export async function generateAlphaCodes(auth, callback, { waveId, count, type, maxUses, expiresAt }) {
    await postRequestBackendDataV2(auth, callback, ENDPOINTS.ALPHA.CODES_GENERATE, {
        waveId,
        count,
        type,
        maxUses,
        expiresAt,
    });
}

/**
 * Deactivate an invite code (admin)
 */
export async function deactivateAlphaCode(auth, callback, codeId) {
    await postRequestBackendDataV2(auth, callback, ENDPOINTS.ALPHA.CODE_DEACTIVATE(codeId), {}, 'DELETE');
}

// Waitlist

/**
 * Get waitlist entries (admin, paginated)
 */
export async function getAlphaWaitlist(auth, callback, page = 0, size = 50) {
    await getRequestBackendDataV2(auth, callback, ENDPOINTS.ALPHA.ADMIN_WAITLIST(page, size));
}

/**
 * Invite users from waitlist (admin)
 */
export async function inviteFromWaitlist(auth, callback, count, waveId) {
    await postRequestBackendDataV2(auth, callback, ENDPOINTS.ALPHA.WAITLIST_INVITE, { count, waveId });
}

/**
 * Delete a waitlist entry (admin)
 */
export async function deleteWaitlistEntry(auth, callback, entryId) {
    await postRequestBackendDataV2(auth, callback, ENDPOINTS.ALPHA.ADMIN_WAITLIST_ENTRY(entryId), {}, 'DELETE');
}

/**
 * Promote a single waitlist entry (admin)
 */
export async function promoteWaitlistEntry(auth, callback, entryId, waveId) {
    await postRequestBackendDataV2(auth, callback, ENDPOINTS.ALPHA.WAITLIST_PROMOTE(entryId), { waveId });
}

// Stats

/**
 * Get alpha access statistics (admin)
 */
export async function getAlphaStats(auth, callback) {
    await getRequestBackendDataV2(auth, callback, ENDPOINTS.ALPHA.STATS);
}

// ==================== ERROR CODE MESSAGES ====================

export const INVITE_CODE_ERROR_MESSAGES = {
    INVALID: 'Invalid invite code',
    EXPIRED: 'This code has expired',
    MAX_USES: 'This code has reached its usage limit',
    WAVE_FULL: 'This wave is currently full',
    WAVE_INACTIVE: 'This wave is not active yet',
    WAVE_NOT_FOUND: 'Wave not found',
    DEACTIVATED: 'This code has been deactivated',
    ALREADY_HAS_ACCESS: 'You already have alpha access',
    INVITE_CODE_REQUIRED: 'An invite code is required to sign up',
    FREE_TIER_FULL: 'Free spots are full — join the waitlist to be notified when a spot opens',
    NETWORK_ERROR: 'Network error, please try again',
};

export function getInviteCodeErrorMessage(errorCode) {
    return INVITE_CODE_ERROR_MESSAGES[errorCode] || 'An error occurred';
}
